<template>
  <div class="app-shell">
    <v-navigation-drawer
      v-model="drawer"
      app
      :permanent="!isMobile"
      :temporary="isMobile"
      :mini-variant="collapsed"
      mini-variant-width="80"
      width="272"
      class="shell-drawer"
      :class="{ 'shell-drawer--mini': collapsed }"
    >
      <div class="drawer-inner">
        <div class="drawer-brand">
          <img src="../../assets/Images/ecoframe_logo.png" alt="" class="brand-logo" />
          <div v-if="!collapsed" class="brand-text">
            <div class="brand-name">ECOFRAME</div>
            <div class="brand-panel">{{ panelLabel }}</div>
          </div>
          <v-btn
            v-if="isMobile"
            icon
            small
            class="ml-auto"
            aria-label="Close menu"
            @click="drawer = false"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <nav class="drawer-nav">
          <div v-for="group in navGroups" :key="group.title || 'main'" class="nav-group">
            <div v-if="group.title && !collapsed" class="nav-section">{{ group.title }}</div>
            <div v-else-if="group.title" class="nav-divider"></div>
            <template v-for="item in group.items">
              <!-- Collapsible group of links (opens the first link when the sidebar is collapsed) -->
              <div v-if="item.children" :key="'tree-' + item.name" class="nav-tree">
                <v-tooltip right :disabled="!collapsed" content-class="nav-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <router-link
                        v-if="collapsed"
                        :to="item.children[0].route"
                        class="nav-item"
                        :class="{ 'nav-item--active': isActive(item) }"
                      >
                        <v-icon class="nav-icon">{{ item.icon }}</v-icon>
                      </router-link>
                      <button
                        v-else
                        type="button"
                        class="nav-item nav-item--parent"
                        :class="{ 'nav-item--parent-active': isActive(item), 'nav-item--open': isGroupOpen(item) }"
                        :aria-expanded="isGroupOpen(item) ? 'true' : 'false'"
                        @click="toggleGroup(item)"
                      >
                        <v-icon class="nav-icon">{{ item.icon }}</v-icon>
                        <span class="nav-label">{{ item.name }}</span>
                        <v-icon class="nav-caret">mdi-chevron-down</v-icon>
                      </button>
                    </div>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
                <v-expand-transition>
                  <div v-show="!collapsed && isGroupOpen(item)" class="nav-children">
                    <router-link
                      v-for="child in item.children"
                      :key="child.route"
                      :to="child.route"
                      class="nav-child"
                      :class="{ 'nav-child--active': isActive(child) }"
                    >
                      <v-icon class="nav-child-icon">{{ child.icon }}</v-icon>
                      <span class="nav-label">{{ child.name }}</span>
                    </router-link>
                  </div>
                </v-expand-transition>
              </div>
              <v-tooltip
                v-else
                :key="item.route"
                right
                :disabled="!collapsed"
                content-class="nav-tooltip"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <router-link
                      :to="item.route"
                      class="nav-item"
                      :class="{ 'nav-item--active': isActive(item) }"
                    >
                      <v-icon class="nav-icon">{{ item.icon }}</v-icon>
                      <span class="nav-label">{{ item.name }}</span>
                    </router-link>
                  </div>
                </template>
                <span>{{ item.name }}</span>
              </v-tooltip>
            </template>
          </div>
        </nav>

        <div class="drawer-footer">
          <div class="user-card">
            <div class="user-avatar">{{ userInitials }}</div>
            <div v-if="!collapsed" class="user-meta">
              <div class="user-name">{{ userName }}</div>
              <div class="user-role">{{ userRole }}</div>
            </div>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="user-logout"
                  :class="{ 'ml-auto': !collapsed }"
                  aria-label="Logout"
                  v-bind="attrs"
                  v-on="on"
                  @click="appLogout"
                >
                  <v-icon small color="white">mdi-logout</v-icon>
                </v-btn>
              </template>
              <span>Logout</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <AppHeader
      :title="pageTitle"
      :subtitle="panelLabel"
      :is-mobile="isMobile"
      :mini="mini"
      :user-name="userName"
      :user-role="userRole"
      :user-initials="userInitials"
      @toggle-sidebar="toggleMini"
      @logout="appLogout"
    />

    <div class="shell-content mainbg4" :class="{ 'shell-content--mobile': isMobile }">
      <router-view></router-view>
    </div>

    <nav v-if="isMobile" class="bottom-nav" aria-label="Quick navigation">
      <router-link
        v-for="item in bottomItems"
        :key="item.route"
        :to="item.route"
        class="bottom-nav-item"
        :class="{ 'bottom-nav-item--active': isActive(item) }"
      >
        <span class="bottom-nav-icon"><v-icon>{{ item.icon }}</v-icon></span>
        <span class="bottom-nav-label">{{ item.short || item.name }}</span>
      </router-link>
      <button type="button" class="bottom-nav-item" @click="drawer = true">
        <span class="bottom-nav-icon"><v-icon>mdi-menu</v-icon></span>
        <span class="bottom-nav-label">Menu</span>
      </button>
    </nav>
  </div>
</template>
<script>
import AppHeader from "@/components/Common/appHeader";
import { getUserType, getNavItems, panelLabels } from "@/components/Common/navConfig";

const MINI_KEY = "sidebarMini";

export default {
  components: {
    AppHeader,
  },
  data() {
    let mini = false;
    try {
      mini = localStorage.getItem(MINI_KEY) === "true";
    } catch (e) {
      mini = false;
    }
    return {
      drawer: !this.$vuetify.breakpoint.smAndDown,
      mini,
      // Open / closed state of collapsible nav groups the user toggled, by name
      openGroups: {},
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    collapsed() {
      return this.mini && !this.isMobile;
    },
    userType() {
      return getUserType();
    },
    panelLabel() {
      return panelLabels[this.userType];
    },
    navItems() {
      return getNavItems(this.userType);
    },
    navGroups() {
      const groups = [];
      this.navItems.forEach((item) => {
        const title = item.section || null;
        let group = groups.find((g) => g.title === title);
        if (!group) {
          group = { title, items: [] };
          groups.push(group);
        }
        group.items.push(item);
      });
      return groups;
    },
    bottomItems() {
      return this.navItems.filter((item) => item.bottom).slice(0, 4);
    },
    pageTitle() {
      const links = [];
      this.navItems.forEach((item) => links.push(...(item.children || [item])));
      const current = links.find((item) => this.isActive(item));
      if (current) return current.name;
      const name = this.$route.name || "";
      return name
        .replace(/[-_]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    userName() {
      return localStorage.getItem("username") || "User";
    },
    userRole() {
      return localStorage.getItem("role") || this.panelLabel;
    },
    userInitials() {
      const parts = this.userName.trim().split(/\s+/).filter(Boolean);
      const initials = parts
        .slice(0, 2)
        .map((p) => p.charAt(0))
        .join("");
      return (initials || "U").toUpperCase();
    },
  },
  watch: {
    isMobile(value) {
      this.drawer = !value;
    },
    $route() {
      if (this.isMobile) this.drawer = false;
      // Opening a page inside a group shows that group's links
      this.navItems.forEach((item) => {
        if (item.children && this.isActive(item)) this.$set(this.openGroups, item.name, true);
      });
    },
  },
  methods: {
    isActive(item) {
      if (item.children) return item.children.some((child) => this.isActive(child));
      return this.$route.path === item.route;
    },
    isGroupOpen(item) {
      const toggled = this.openGroups[item.name];
      return toggled === undefined ? this.isActive(item) : toggled;
    },
    toggleGroup(item) {
      this.$set(this.openGroups, item.name, !this.isGroupOpen(item));
    },
    toggleMini() {
      this.mini = !this.mini;
      try {
        localStorage.setItem(MINI_KEY, String(this.mini));
      } catch (e) {
        // Collapsed state is only a convenience; ignore storage failures.
      }
    },
    appLogout() {
      this.$store.commit("logoutUser", true);
    },
  },
};
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
}

/* ---------- Drawer ---------- */
.shell-drawer {
  background: linear-gradient(180deg, #14532d 0%, #15803d 60%, #16a34a 100%) !important;
  border: none !important;
  box-shadow: 4px 0 24px rgba(15, 23, 42, 0.12);
}

.drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.shell-drawer--mini .drawer-brand {
  justify-content: center;
  padding: 18px 0 16px;
}

.brand-logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
  flex-shrink: 0;
  padding: 3px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.brand-name {
  font-size: 19px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: 1px;
  line-height: 1.1;
}

.brand-panel {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
}

.drawer-nav {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px 16px;
}

.drawer-nav::-webkit-scrollbar {
  width: 4px;
}

.drawer-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.nav-section {
  margin: 16px 10px 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.5);
}

.nav-group:first-child .nav-section {
  margin-top: 6px;
}

.nav-divider {
  height: 1px;
  margin: 12px 14px;
  background: rgba(255, 255, 255, 0.12);
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 12px;
  margin-bottom: 2px;
  border-radius: 12px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.82);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.nav-icon {
  color: inherit !important;
  font-size: 21px !important;
  flex-shrink: 0;
}

.nav-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-item--active,
.nav-item--active:hover {
  background: #ffffff;
  color: #15803d;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.nav-item--active .nav-label {
  font-weight: 800;
}

.nav-item--active::before {
  content: "";
  position: absolute;
  left: -12px;
  top: 10px;
  bottom: 10px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: #fbbf24;
}

/* Collapsible group */
.nav-item--parent {
  width: 100%;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.nav-item--parent:focus-visible,
.nav-child:focus-visible {
  outline: 2px solid #fbbf24;
  outline-offset: -2px;
}

.nav-item--parent-active {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.nav-caret {
  margin-left: auto;
  color: inherit !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.nav-item--open .nav-caret {
  transform: rotate(180deg);
}

.nav-children {
  position: relative;
  margin: 2px 0 6px 22px;
  padding-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.18);
}

.nav-child {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 36px;
  padding: 0 10px;
  margin-bottom: 2px;
  border-radius: 10px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.75);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.nav-child:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.nav-child .nav-label {
  font-size: 13.5px;
}

.nav-child-icon {
  flex-shrink: 0;
  color: inherit !important;
  font-size: 17px !important;
}

.nav-child--active,
.nav-child--active:hover {
  background: #ffffff;
  color: #15803d;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.nav-child--active .nav-label {
  font-weight: 800;
}

.shell-drawer--mini .drawer-nav {
  padding: 10px 14px 16px;
}

.shell-drawer--mini .nav-item {
  justify-content: center;
  padding: 0;
}

.shell-drawer--mini .nav-label {
  display: none;
}

.shell-drawer--mini .nav-item--active::before {
  left: -14px;
}

.drawer-footer {
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
}

.shell-drawer--mini .user-card {
  flex-direction: column;
  padding: 10px 0;
  background: transparent;
}

.user-avatar {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fbbf24;
  color: #14532d;
  font-size: 14px;
  font-weight: 800;
}

.user-meta {
  min-width: 0;
}

.user-name,
.user-role {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-name {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.user-logout {
  background: rgba(239, 68, 68, 0.25);
}

.user-logout:hover {
  background: rgba(239, 68, 68, 0.6);
}

/* ---------- Content ---------- */
.shell-content {
  min-height: calc(100vh - 68px);
}

.shell-content--mobile {
  min-height: calc(100vh - 58px);
  padding-bottom: calc(72px + env(safe-area-inset-bottom));
}

/* ---------- Mobile bottom bar ---------- */
.bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: flex;
  padding: 6px 6px calc(6px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
}

.bottom-nav-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 2px;
  border: none;
  background: none;
  cursor: pointer;
  text-decoration: none;
  color: #64748b;
  font-family: inherit;
}

.bottom-nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 30px;
  border-radius: 999px;
  transition: background-color 0.2s ease;
}

.bottom-nav-icon .v-icon {
  color: inherit !important;
  font-size: 22px !important;
}

.bottom-nav-label {
  max-width: 100%;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bottom-nav-item--active {
  color: #15803d;
}

.bottom-nav-item--active .bottom-nav-icon {
  background: rgba(21, 128, 61, 0.12);
}

.bottom-nav-item--active .bottom-nav-label {
  font-weight: 800;
}
</style>

<style>
/* Shared legacy helpers used by pages across the app. */
.activated {
  color: white !important;
}

.demo-bg {
  opacity: 0.6;
}

.mainbg2 {
  background-image: linear-gradient(269.6deg, #e8f4ec -31.66%, #e8f4ec);
}

.item-text {
  display: flex;
  align-items: center;
  font-size: 18px;
  color: black;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  margin-left: 8px;
}

.fixedSidebar {
  position: -webkit-sticky;
  position: sticky;
  top: 50;
  border-right: 2px solid rgba(21, 128, 61, 0.2) !important;
}

/* Sidebar nav text */
.fixedSidebar span,
.fixedSidebar .v-list-item-title {
  color: #14532d !important;
  font-weight: 500;
}

/* Sidebar divider */
.fixedSidebar .v-divider {
  border-color: rgba(21, 128, 61, 0.15) !important;
}

/* Sidebar list-group expand icon */
.fixedSidebar .v-list-group__header .v-icon {
  color: #14532d !important;
}

@media only screen and (min-device-width: 360px) and (max-device-width: 640px) {
  .fixedSidebar {
    display: none !important;
  }
}

.content {
  height: 40%;
}

/* Active sidebar item */
.mainbg5 {
  background-color: #bbf7d0 !important;
  background-image: none !important;
  border-left: 4px solid #15803d !important;
  border-radius: 0 6px 6px 0;
  padding: 4px 4px 4px 8px !important;
}

.mainbg6 {
  background-color: #bbf7d0 !important;
  background-image: none !important;
  border-left: 4px solid #15803d !important;
  border-radius: 0 6px 6px 0;
  padding: 4px 4px 4px 8px !important;
}

.mainbg5 span,
.mainbg6 span {
  color: #0a3320 !important;
  font-weight: 700 !important;
}

.activated .content {
  background-color: #e8f4ec;
}

.activated .mainbg6 {
  background-color: #bbf7d0;
}
.nav-tooltip {
  font-weight: 600;
}
</style>
